import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { initializeDB } from '../../config/db';
import { EQUIPMENT_STORE, EQUIP_LOCATION_STORE } from '../../config/constants';
import { commonDataGridStyles } from '../../styles/dataGridStyles';

const TwoLineHeader = ({ line1, line2 }) => (
    <Box sx={{ lineHeight: 1.2 }}>
        <Box sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{line1}</Box>
        <Box sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary' }}>{line2}</Box>
    </Box>
);

const InternalEquipmentTable = ({ selectedJobCodes, jobs }) => {
    const [equipment, setEquipment] = useState([]);
    const [equipLocations, setEquipLocations] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            const db = await initializeDB();
            const [allEquipment, allLocations] = await Promise.all([
                db.getAll(EQUIPMENT_STORE),
                db.getAll(EQUIP_LOCATION_STORE),
            ]);
            setEquipment(allEquipment);
            setEquipLocations(allLocations);
            setLoading(false);
        };
        loadData();
    }, []);

    const jobLocationMap = useMemo(() => {
        const map = {};
        if (jobs) {
            jobs.forEach(j => {
                if (j.Job) {
                    map[j.Job.trim()] = j.location || j.Location || '';
                }
            });
        }
        return map;
    }, [jobs]);

    const columns = useMemo(() => [
        {
            field: 'equipment',
            headerName: 'Equip',
            width: 90,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {params.value || '-'}
                </Typography>
            ),
        },
        {
            field: 'description',
            headerName: 'Description',
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (
                <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word', lineHeight: 1.3, fontSize: '0.8rem' }}>
                    {params.value || '-'}
                </Box>
            ),
        },
        {
            field: 'job',
            headerName: 'Job',
            flex: 0.5,
            minWidth: 110,
            renderHeader: () => <TwoLineHeader line1="Job" line2="Location" />,
            renderCell: (params) => (
                <Box sx={{ py: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.2 }}>
                        {params.value || '-'}
                    </Typography>
                    {params.row.locationName && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2, display: 'block' }}>
                            {params.row.locationName}
                        </Typography>
                    )}
                </Box>
            ),
        },
    ], []);

    // Join: EquipLocation (latest per equipment) + Equipment + Job
    const rows = useMemo(() => {
        if (!selectedJobCodes || selectedJobCodes.length === 0) return [];

        const jobSet = new Set(selectedJobCodes.map(c => c.trim()));

        const jobLocations = equipLocations.filter(
            loc => loc.location && jobSet.has(loc.location.trim()) && loc.status !== false
        );

        const latestByEquip = {};
        for (const loc of jobLocations) {
            const key = loc.equipment?.trim();
            if (!key) continue;
            const existing = latestByEquip[key];
            if (!existing) {
                latestByEquip[key] = loc;
            } else {
                const existDate = existing.eDate ? new Date(existing.eDate).getTime() : 0;
                const locDate = loc.eDate ? new Date(loc.eDate).getTime() : 0;
                if (locDate > existDate || (locDate === existDate && (loc.id || 0) > (existing.id || 0))) {
                    latestByEquip[key] = loc;
                }
            }
        }

        const equipMap = {};
        for (const eq of equipment) {
            if (eq.equipment && eq.status !== false) {
                equipMap[eq.equipment.trim()] = eq;
            }
        }

        return Object.values(latestByEquip).map((loc) => {
            const eq = equipMap[loc.equipment?.trim()] || {};
            const jobNum = loc.location?.trim() || '';
            return {
                _rowId: `${loc.id}`,
                equipment: loc.equipment,
                description: eq.description || '',
                job: jobNum,
                locationName: jobLocationMap[jobNum] || '',
            };
        });
    }, [selectedJobCodes, equipment, equipLocations, jobLocationMap]);

    return (
        <Box sx={{ flex: 1, overflow: 'hidden', px: 1, pb: 1 }}>
            {rows.length === 0 && !loading ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        No internal equipment found ({selectedJobCodes.length} job{selectedJobCodes.length !== 1 ? 's' : ''} selected)
                    </Typography>
                </Box>
            ) : (
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    getRowId={(row) => row._rowId}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 25 } },
                    }}
                    pageSizeOptions={[25, 50, 100]}
                    disableRowSelectionOnClick
                    rowHeight={52}
                    columnHeaderHeight={52}
                    sx={{
                        ...commonDataGridStyles,
                        '& .MuiDataGrid-columnHeaderTitleContainer': {
                            overflow: 'visible',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            overflow: 'visible',
                            textOverflow: 'clip',
                            whiteSpace: 'normal',
                            lineHeight: 1.2,
                            fontWeight: 600,
                        },
                        '& .MuiDataGrid-menuIcon': {
                            width: 0,
                            minWidth: 0,
                            overflow: 'visible',
                        },
                        '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                        '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#fff' },
                        '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04) !important' },
                        '& .MuiDataGrid-cell': {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            lineHeight: 1.3,
                            display: 'flex',
                            alignItems: 'center',
                        },
                    }}
                />
            )}
        </Box>
    );
};

export default InternalEquipmentTable;
